import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { invoiceApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  Search,
  MoreVertical,
  FileText,
  Send,
  CheckCircle,
  Copy,
  Trash2,
  Download,
  Eye,
  DollarSign,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: FileText },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700', icon: Send },
  viewed: { label: 'Viewed', color: 'bg-purple-100 text-purple-700', icon: Eye },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  partial: { label: 'Partial', color: 'bg-yellow-100 text-yellow-700', icon: DollarSign },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700', icon: AlertCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500', icon: Trash2 },
};

export default function Invoices() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchInvoices();
  }, [statusFilter]);

  const fetchInvoices = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await invoiceApi.getAll(params);
      setInvoices(response.data);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (invoice) => {
    if (!invoice.client_email) {
      toast.error('Client has no email address');
      return;
    }
    try {
      await invoiceApi.send(invoice.id);
      toast.success(`Invoice sent to ${invoice.client_email}`);
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to send invoice');
    }
  };

  const handleMarkPaid = async (invoice) => {
    try {
      await invoiceApi.markPaid(invoice.id);
      toast.success('Invoice marked as paid');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to mark as paid');
    }
  };

  const handleDuplicate = async (invoice) => {
    try {
      const response = await invoiceApi.duplicate(invoice.id);
      toast.success(`Created ${response.data.invoice_number}`);
      navigate(`/invoices/${response.data.id}/edit`);
    } catch (error) {
      toast.error('Failed to duplicate invoice');
    }
  };

  const handleDelete = async (invoice) => {
    const isPaid = invoice.status === 'paid';
    const msg = isPaid
      ? `Cancel PAID invoice ${invoice.invoice_number}? This removes it from your paid totals (use for accidental payments).`
      : `Cancel invoice ${invoice.invoice_number}?`;
    if (!window.confirm(msg)) return;
    try {
      await invoiceApi.delete(invoice.id);
      toast.success('Invoice cancelled');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to cancel invoice');
    }
  };

  const handlePermanentDelete = async (invoice) => {
    if (!window.confirm(`Permanently delete ${invoice.invoice_number}? This cannot be undone.`)) return;
    try {
      await invoiceApi.permanentDelete(invoice.id);
      toast.success('Invoice permanently deleted');
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      const response = await invoiceApi.getPdf(invoice.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoice_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const filteredInvoices = invoices.filter((inv) => {
    // Hide cancelled from the default "all" view; only show them when explicitly filtered
    if (statusFilter === 'all' && inv.status === 'cancelled') return false;
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      inv.invoice_number.toLowerCase().includes(searchLower) ||
      inv.client_name?.toLowerCase().includes(searchLower) ||
      inv.project_name?.toLowerCase().includes(searchLower)
    );
  });

  // Calculate totals
  const totals = {
    outstanding: invoices
      .filter((i) => ['sent', 'viewed', 'overdue', 'partial'].includes(i.status))
      .reduce((sum, i) => sum + (i.total - (i.paid_amount || 0)), 0),
    overdue: invoices
      .filter((i) => i.status === 'overdue')
      .reduce((sum, i) => sum + (i.total - (i.paid_amount || 0)), 0),
    // Actual money received across all non-cancelled invoices (includes partial payments)
    paid: invoices
      .filter((i) => i.status !== 'cancelled')
      .reduce((sum, i) => sum + (i.paid_amount || (i.status === 'paid' ? i.total : 0)), 0),
  };

  const handleExportCsv = async () => {
    try {
      const response = await invoiceApi.exportCsv();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'corbel_invoices_export.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch {
      toast.error('Failed to export CSV');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Invoices</h1>
          <p className="text-muted-foreground">Manage and send invoices to clients</p>
        </div>
        <Button onClick={() => navigate('/invoices/new')} data-testid="create-invoice-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Invoice
        </Button>
        <Button variant="outline" onClick={handleExportCsv} data-testid="export-csv-btn">
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Outstanding</p>
                <p className="text-2xl font-bold">{formatCurrency(totals.outstanding)}</p>
              </div>
              <Clock className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Overdue</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(totals.overdue)}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Paid (All Time)</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(totals.paid)}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search invoices..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="invoice-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Invoice List */}
      {filteredInvoices.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No invoices found</h3>
            <p className="text-muted-foreground mb-4">
              {search || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first invoice to get started'}
            </p>
            {!search && statusFilter === 'all' && (
              <Button onClick={() => navigate('/invoices/new')}>
                <Plus className="h-4 w-4 mr-2" />
                Create Invoice
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-4 font-medium">Invoice</th>
                  <th className="text-left p-4 font-medium">Client</th>
                  <th className="text-left p-4 font-medium">Date</th>
                  <th className="text-left p-4 font-medium">Due</th>
                  <th className="text-right p-4 font-medium">Amount</th>
                  <th className="text-left p-4 font-medium">Status</th>
                  <th className="text-right p-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice) => {
                  const StatusIcon = STATUS_CONFIG[invoice.status]?.icon || FileText;
                  return (
                    <tr
                      key={invoice.id}
                      className="border-b hover:bg-muted/50 cursor-pointer"
                      onClick={() => navigate(`/invoices/${invoice.id}`)}
                      data-testid={`invoice-row-${invoice.id}`}
                    >
                      <td className="p-4">
                        <span className="font-medium">{invoice.invoice_number}</span>
                        {invoice.project_name && (
                          <p className="text-xs text-muted-foreground">{invoice.project_name}</p>
                        )}
                      </td>
                      <td className="p-4">
                        <span>{invoice.client_name}</span>
                        {invoice.client_email && (
                          <p className="text-xs text-muted-foreground">{invoice.client_email}</p>
                        )}
                      </td>
                      <td className="p-4 text-sm">{formatDate(invoice.issue_date)}</td>
                      <td className="p-4 text-sm">{formatDate(invoice.due_date)}</td>
                      <td className="p-4 text-right font-medium">{formatCurrency(invoice.total)}</td>
                      <td className="p-4">
                        <Badge className={`${STATUS_CONFIG[invoice.status]?.color} gap-1`}>
                          <StatusIcon className="h-3 w-3" />
                          {STATUS_CONFIG[invoice.status]?.label}
                        </Badge>
                      </td>
                      <td className="p-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/invoices/${invoice.id}`); }}>
                              <Eye className="h-4 w-4 mr-2" />
                              View
                            </DropdownMenuItem>
                            {invoice.status === 'draft' && (
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/invoices/${invoice.id}/edit`); }}>
                                <FileText className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDownloadPdf(invoice); }}>
                              <Download className="h-4 w-4 mr-2" />
                              Download PDF
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {['draft', 'sent', 'viewed', 'overdue'].includes(invoice.status) && (
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleSend(invoice); }}>
                                <Send className="h-4 w-4 mr-2" />
                                {invoice.status === 'draft' ? 'Send' : 'Resend'}
                              </DropdownMenuItem>
                            )}
                            {['sent', 'viewed', 'overdue', 'partial'].includes(invoice.status) && (
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleMarkPaid(invoice); }}>
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Mark as Paid
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicate(invoice); }}>
                              <Copy className="h-4 w-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {invoice.status !== 'cancelled' && (
                              <DropdownMenuItem
                                onClick={(e) => { e.stopPropagation(); handleDelete(invoice); }}
                                className="text-red-600"
                                data-testid={`cancel-invoice-${invoice.id}`}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Cancel Invoice
                              </DropdownMenuItem>
                            )}
                            {invoice.status === 'cancelled' && (
                              <DropdownMenuItem
                                onClick={(e) => { e.stopPropagation(); handlePermanentDelete(invoice); }}
                                className="text-red-600"
                                data-testid={`delete-invoice-${invoice.id}`}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Permanently
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
